import { createFileRoute, Link, useRouter, notFound, redirect } from "@tanstack/react-router";

/** Old article slugs Google still has indexed -> current slug (301). */
const LEGACY_SLUGS: Record<string, string> = {
  "preventing-weight-regain-after-discontinuation-of-injectable-weight-loss-medicines-part-one":
    "weight-regain-after-wegovy-zepbound-treatment-part-1",
  "preventing-weight-regain-after-discontinuation-of-injectable-weight-loss-medicines-part-two":
    "weight-regain-after-wegovy-zepbound-treatment-part-2",
  "preventing-weight-regain-after-discontinuation-of-injectable-weight-loss-medicines-part-three":
    "weight-regain-after-wegovy-zepbound-treatment-part-3",
};
import { lastmodFor } from "@/lib/route-lastmod";
import { schemaIsoDate } from "@/lib/schema-dates";
import { ORG_REF, PHYSICIAN_ID, PHYSICIAN_REF, PHYSICIAN_SAME_AS } from "@/lib/medical-schema";
import { useEffect } from "react";
import { getPostBySlug, getAdjacentPosts, prefetchPostHtml, type Post } from "@/lib/posts";
import { listEsPosts } from "@/lib/posts-es";

/** English slug -> Spanish slug, for the reciprocal hreflang alternate. */
const ES_SLUG_BY_EN: Record<string, string> = Object.fromEntries(
  listEsPosts().map((p) => [p.enSlug, p.slug]),
);
import { ShareButtons } from "@/components/site/ShareButtons";
import { resolveOgImage } from "@/lib/og-image";

export const Route = createFileRoute("/dr-fitzer-dissects-the-news/$slug")({
  // Post content is immutable at build time — keep the loaded post in the
  // router cache so revisits skip the loader (and the HTML dynamic import).
  staleTime: Infinity,
  gcTime: 60 * 60_000, // 1 hour
  loader: async ({ params }): Promise<{ post: Post }> => {
    const legacy = LEGACY_SLUGS[params.slug];
    if (legacy) {
      throw redirect({
        to: "/dr-fitzer-dissects-the-news/$slug",
        params: { slug: legacy },
        statusCode: 301,
      });
    }
    const post = await getPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    const BRAND = "Virginia Bariatric";
    const withBrand = (t: string) =>
      t.includes(BRAND) ? t : `${t.replace(/\s*\|\s*(Dr\.? ?Fitzer( Dissects the News)?|VA Bariatric|Virginia Bariatric)$/, "")} | ${BRAND}`;
    const title = withBrand(
      loaderData ? (loaderData.post.seoTitle ?? loaderData.post.title) : "Post",
    );
    const description =
      loaderData?.post.seoDescription ??
      loaderData?.post.excerpt?.slice(0, 155) ??
      "";
    const ogTitle = loaderData?.post.ogTitle ?? title;
    const ogDescription = loaderData?.post.ogDescription ?? description;
    const cardStem = loaderData?.post.ogCard ?? `og-${loaderData?.post.slug}`;
    const canonicalUrl = loaderData
      ? `https://virginiabariatric.com/dr-fitzer-dissects-the-news/${loaderData.post.slug}`
      : undefined;
    const meta = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: ogTitle },
      { property: "og:description", content: ogDescription },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: ogTitle },
      { name: "twitter:description", content: ogDescription },
    ];
    if (canonicalUrl) {
      meta.push({ property: "og:url", content: canonicalUrl });
    }
    const autoOgImage = loaderData
      ? `https://virginiabariatric.com/${cardStem}.png`
      : undefined;
    // Precedence: branded auto card → post featuredImage (custom hero) → default.
    // resolveOgImage logs the winning source in dev for quick diagnosis.
    const imageUrl = resolveOgImage({
      slug: loaderData?.post.slug,
      autoCard: autoOgImage,
      featuredImage: loaderData?.post.featuredImage ?? undefined,
    }).url;

    const squareOgImage = loaderData
      ? `https://virginiabariatric.com/${cardStem}-square.png`
      : undefined;
    const wideOgImage = loaderData
      ? `https://virginiabariatric.com/${cardStem}-wide.png`
      : undefined;
    // 4:5 (1080x1350) — Instagram's feed/grid crop.
    const portraitOgImage = loaderData
      ? `https://virginiabariatric.com/${cardStem}-portrait.png`
      : undefined;
    if (loaderData) {
      const imageAlt = loaderData.post.ogImageAlt ?? `${loaderData.post.title} — Dr. Fitzer Dissects the News`;
      // Only one og:image is emitted: the head manager dedupes by property, so
      // extra crops would silently overwrite the primary card's dimensions.
      // The 1:1 and 16:9 crops stay available in the Article schema below.
      meta.push({ property: "og:image", content: imageUrl });
      meta.push({ property: "og:image:secure_url", content: imageUrl });
      meta.push({ property: "og:image:type", content: "image/png" });
      meta.push({ property: "og:image:alt", content: imageAlt });
      meta.push({ property: "og:image:width", content: "2400" });
      meta.push({ property: "og:image:height", content: "1260" });
      meta.push({ name: "twitter:image", content: imageUrl });
      meta.push({ name: "twitter:image:alt", content: imageAlt });
    }

    if (!loaderData) {
      meta.push({ name: "robots", content: "noindex" });
    }
    const scripts = canonicalUrl && loaderData
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              headline: loaderData.post.title,
              description,
              datePublished: schemaIsoDate(loaderData.post.date),
              dateModified: lastmodFor(`/dr-fitzer-dissects-the-news/${loaderData.post.slug}`) ?? schemaIsoDate(loaderData.post.date),
              mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
              url: canonicalUrl,
              image: [
                {
                  "@type": "ImageObject",
                  contentUrl: imageUrl,
                  url: imageUrl,
                  caption: loaderData.post.title,
                  width: 2400,
                  height: 1260,
                  representativeOfPage: true,
                },
                // Same card as WEBP — smaller and cleaner for crawlers that
                // accept it; the PNG above stays the universal fallback.
                {
                  "@type": "ImageObject",
                  contentUrl: imageUrl.replace(/\.png$/, ".webp"),
                  url: imageUrl.replace(/\.png$/, ".webp"),
                  caption: loaderData.post.title,
                  encodingFormat: "image/webp",
                  width: 2400,
                  height: 1260,
                },
                ...(squareOgImage && wideOgImage
                  ? [
                      {
                        "@type": "ImageObject",
                        contentUrl: squareOgImage,
                        url: squareOgImage,
                        caption: loaderData.post.title,
                        width: 1440,
                        height: 1440,
                      },
                      {
                        "@type": "ImageObject",
                        contentUrl: wideOgImage,
                        url: wideOgImage,
                        caption: loaderData.post.title,
                        width: 1920,
                        height: 1080,
                      },
                      ...(portraitOgImage
                        ? [
                            {
                              "@type": "ImageObject",
                              contentUrl: portraitOgImage,
                              url: portraitOgImage,
                              caption: loaderData.post.title,
                              width: 1080,
                              height: 1350,
                            },
                          ]
                        : []),
                    ]
                  : []),
                ...(loaderData.post.images ?? []).map((img) => ({
                  "@type": "ImageObject",
                  contentUrl: `https://virginiabariatric.com${img.url}`,
                  url: `https://virginiabariatric.com${img.url}`,
                  name: img.name,
                  caption: img.caption,
                  description: img.description,
                  width: img.width,
                  height: img.height,
                  inLanguage: "en-US",
                })),
              ],
              inLanguage: "en-US",
              isPartOf: {
                "@type": "Blog",
                "@id":
                  "https://virginiabariatric.com/dr-fitzer-dissects-the-news#blog",
                name: "Dr. Fitzer Dissects the News",
                url: "https://virginiabariatric.com/dr-fitzer-dissects-the-news",
              },
              author: {
                "@type": "Person",
                "@id": PHYSICIAN_ID,
                name: "Matthew Fitzer, MD",
                honorificSuffix: "MD",
                url: "https://virginiabariatric.com/about",
                mainEntityOfPage: "https://virginiabariatric.com/about",
                sameAs: PHYSICIAN_SAME_AS,
                jobTitle: "Bariatric Surgeon",
                knowsAbout: ["Bariatric surgery", "Obesity medicine", "Weight-loss medicine"],
                worksFor: ORG_REF,
              },
              // reviewedBy is not valid on NewsArticle/Article per Schema.org;
              // the author Person already carries the same physician profile.
              publisher: {
                "@type": "Organization",
                "@id": "https://virginiabariatric.com/#organization",
                name: "Virginia Bariatric Surgery",
                url: "https://virginiabariatric.com",
                logo: {
                  "@type": "ImageObject",
                  url: "https://virginiabariatric.com/logo.png",
                },
              }

            }),
          },
          // BreadcrumbList is emitted sitewide by __root.tsx — do not duplicate it here.
          // The author Person is the sitewide #physician-fitzer entity referenced above.
        ]
      : [];


    return {
      meta,
      links: canonicalUrl
        ? [
            { rel: "canonical", href: canonicalUrl },
            ...(loaderData && ES_SLUG_BY_EN[loaderData.post.slug]
              ? [
                  {
                    rel: "alternate",
                    hrefLang: "en",
                    href: canonicalUrl,
                  },
                  {
                    rel: "alternate",
                    hrefLang: "es",
                    href: `https://virginiabariatric.com/es/el-doctor-analiza-las-noticias/${ES_SLUG_BY_EN[loaderData.post.slug]}`,
                  },
                  {
                    rel: "alternate",
                    hrefLang: "x-default",
                    href: canonicalUrl,
                  },
                ]
              : []),
          ]
        : [],
      scripts,
    };
  },
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <main className="mx-auto max-w-4xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 py-16">
        <h1 className="text-3xl font-serif mb-4">Post unavailable</h1>
        <p className="text-muted-foreground">We couldn't load this post right now.</p>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        <button
          type="button"
          onClick={() => {
            reset();
            router.invalidate();
          }}
          className="mt-6 inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Try again
        </button>
      </main>
    );
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-4xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 py-16">
      <h1 className="text-3xl font-serif mb-4">Post not found</h1>
      <p className="text-muted-foreground">
        This article may have been moved or removed.
      </p>
      <Link
        to="/dr-fitzer-dissects-the-news"
        className="mt-6 inline-block text-brand-mid font-medium hover:underline"
      >
        Back to All Posts
      </Link>
    </main>
  ),
  component: PostPage,
});

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function PostPage() {
  const { post } = Route.useLoaderData();
  const router = useRouter();
  const { prev, next } = getAdjacentPosts(post.slug);

  // Prefetch adjacent post HTML + route chunks during idle time so that
  // clicking Prev/Next feels instant without inflating the initial payload.
  useEffect(() => {
    const win = typeof window !== "undefined" ? window : null;
    if (!win) return;
    const ric: (cb: () => void) => number =
      // requestIdleCallback isn't in Safari; fall back to a small timeout.
      (win as unknown as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback ?? ((cb) => win.setTimeout(cb, 300));
    const cic: (h: number) => void =
      (win as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback ?? ((h) => win.clearTimeout(h));
    const handle = ric(() => {
      for (const adj of [prev, next]) {
        if (!adj) continue;
        prefetchPostHtml(adj.slug);
        void router.preloadRoute({
          to: "/dr-fitzer-dissects-the-news/$slug",
          params: { slug: adj.slug },
        });
      }
    });
    return () => cic(handle);
  }, [router, prev, next]);

  return (
    <main className="mx-auto max-w-4xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-8 pb-16">
      <nav className="mb-6 text-sm">
        <Link
          to="/dr-fitzer-dissects-the-news"
          className="text-brand-mid hover:underline"
        >
          ← Back to All Posts
        </Link>
      </nav>

      <header>
        <h1 className="text-[1.9125rem] md:text-[2.55rem] font-serif leading-tight text-brand-mid">
          {post.title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{formatDate(post.date)}</p>
        {post.subtitle && (
          <p className="mt-4 text-xl md:text-2xl font-serif leading-snug text-brand-mid">
            {post.subtitle}
          </p>
        )}
      </header>

      {post.featuredImage && (
        <figure className="mt-6">
          <img
            src={post.featuredImage}
            alt={post.featuredImageAlt ?? post.title}
            width={1600}
            height={729}
            className="w-full h-auto rounded-md aspect-video object-cover bg-muted"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          {post.featuredImageCaption && (
            <figcaption className="mt-2 text-center text-sm leading-snug text-muted-foreground">
              {post.featuredImageCaption}
            </figcaption>
          )}
        </figure>
      )}

      <article
        className="wp-post-content mt-8 leading-[1.6] md:text-[1.125rem] [&_p]:mb-4 [&_a]:text-brand-mid [&_a]:underline [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-brand-mid [&_h3]:font-serif [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-brand-mid [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_img]:rounded-md [&_img]:my-6 [&_figure]:my-6 [&_figure_img]:my-0 [&_figcaption]:mt-2 [&_figcaption]:text-center [&_figcaption]:text-sm [&_figcaption]:leading-snug [&_figcaption]:text-muted-foreground [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4 [&_sup]:text-[0.68em] [&_sup]:leading-none [&_sup]:align-super [&_sup]:ml-[0.1em] [&_sup_a]:no-underline [&_sup_a]:font-medium [&_sup_a]:hover:underline [&_.nowrap]:whitespace-nowrap [&_ol_li]:scroll-mt-28 [&_ol_li]:target:bg-brand-mid/10 [&_table]:my-6 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-2 [&_th]:text-left [&_td]:border [&_td]:border-border [&_td]:p-2 [&_td]:align-top [&_tbody_th[scope=row]]:bg-transparent [&_tbody_th[scope=row]]:font-normal [&_a]:focus-visible:outline [&_a]:focus-visible:outline-2 [&_a]:focus-visible:outline-offset-2 [&_a]:focus-visible:outline-brand-mid [&_ol_li]:focus-visible:outline-none [&_ol_li]:focus:bg-brand-mid/10"
        dangerouslySetInnerHTML={{ __html: post.contentHtml }}
      />

      <ShareButtons
        url={`https://virginiabariatric.com/dr-fitzer-dissects-the-news/${post.slug}`}
        title={post.title}
        instagramImage={`https://virginiabariatric.com/${post.ogCard ?? `og-${post.slug}`}-portrait.png`}
      />

      {(prev || next) && (
        <nav
          aria-label="More posts"
          className="mt-12 grid gap-4 border-t pt-8 sm:grid-cols-2"
        >
          {prev ? (
            <Link
              to="/dr-fitzer-dissects-the-news/$slug"
              params={{ slug: prev.slug }}
              className="group block rounded-md border p-4 hover:border-brand-mid"
            >
              <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                Previous
              </span>
              <span className="mt-1 block font-serif text-lg text-brand-mid group-hover:underline">
                {prev.title}
              </span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to="/dr-fitzer-dissects-the-news/$slug"
              params={{ slug: next.slug }}
              className="group block rounded-md border p-4 text-right hover:border-brand-mid sm:text-right"
            >
              <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                Next
              </span>
              <span className="mt-1 block font-serif text-lg text-brand-mid group-hover:underline">
                {next.title}
              </span>
            </Link>
          ) : (
            <span />
          )}
        </nav>
      )}
    </main>
  );
}
